import { ChronoUnit, DateTimeFormatter, DayOfWeek, Instant, LocalDate, Month, Period, YearMonth, ZoneId } from "@js-joda/core";
import bignumber from "bignumber.js";
import { Locale } from "@js-joda/locale_en";
import zod, { z } from "zod";
import axios from "axios";
import { parsePhoneNumberWithError } from "libphonenumber-js";
const DateFormat = DateTimeFormatter.ofPattern("LLL d, yyyy").withLocale(Locale.US);
const MonthFormat = DateTimeFormatter.ofPattern("LLL").withLocale(Locale.US);
function monthsToYears(months) {
    if (null == months) return null;
    const years = months / 12;
    return Math.round((years + Number.EPSILON) * 100) / 100;
}
function toBigNumber(value) {
    if (null == value) return value;
    if (value instanceof bignumber) return value;
    return new bignumber(value);
}
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
function toLocalDate(value) {
    if (null == value) return value;
    return LocalDate.parse(value);
}
function addressFromJSON(json) {
    return Object.freeze({
        ID: json.ID,
        line1: json.line1,
        line2: json.line2,
        city: json.city,
        state: json.state,
        zip_code: json.zip_code,
        taxes: json.taxes ? {
            year: json.taxes.year,
            amount: toBigNumber(json.taxes.amount)
        } : null,
        lastSaleDate: json.lastSaleDate ? LocalDate.parse(json.lastSaleDate) : null,
        value: toBigNumber(json.value),
        valueRange: json.valueRange ? [
            toBigNumber(json.valueRange[0]),
            toBigNumber(json.valueRange[1])
        ] : null
    });
}
const StateSchema = zod["enum"]([
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY"
]);
const StateNames = {
    AL: "Alabama",
    AK: "Alaska",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    FL: "Florida",
    GA: "Georgia",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming"
};
const ZERO = new bignumber(0);
const isValidBig = (v)=>bignumber.isBigNumber(v) && v.isFinite() && !v.isNaN();
function withBigMethods(schema) {
    const wrap = (s)=>withBigMethods(s);
    const s = schema;
    s.min = (v, msg)=>{
        const bn = new bignumber(v);
        return wrap(schema.refine((x)=>{
            if (null === x) return true;
            return x.gte(bn);
        }, {
            message: msg ?? `Must be ≥ ${bn.toString()}`
        }));
    };
    s.max = (v, msg)=>{
        const bn = new bignumber(v);
        return wrap(schema.refine((x)=>{
            if (null === x) return true;
            return x.lte(bn);
        }, {
            message: msg ?? `Must be ≤ ${bn.toString()}`
        }));
    };
    s.positive = (msg)=>wrap(schema.refine((x)=>{
            if (null === x) return true;
            return x.gt(0);
        }, {
            message: msg ?? "Must be > 0"
        }));
    s.nonnegative = (msg)=>wrap(schema.refine((x)=>{
            if (null === x) return true;
            return x.gte(0);
        }, {
            message: msg ?? "Must be ≥ 0"
        }));
    s.maxDp = (n, msg)=>wrap(schema.refine((x)=>(x?.decimalPlaces() ?? 0) <= n, {
            message: msg ?? `Must have ≤ ${n} decimal places`
        }));
    return s;
}
const BigNumberSchema = withBigMethods(z.preprocess((input)=>{
    if (null === input) return null;
    if (bignumber.isBigNumber(input)) return input;
    if ("number" == typeof input) return new bignumber(input);
    if ("string" == typeof input) try {
        return new bignumber(input.trim());
    } catch  {
        return null;
    }
    throw new Error(`Unknown BigNumber input ${typeof input}`);
}, z.custom(isValidBig, {
    message: "Invalid number"
})));
const Config = {
    Subscription: {
        InitialPhaseMonths: 3,
        InitialLookupKey: "initial_monthly",
        StandardLookupKey: "standard_monthly"
    }
};
function purchaseOfferFromJSON(json) {
    return {
        ...json,
        prepaidItems: json.prepaidItems.map(prepaidItemFromJson),
        discountPoints: toBigNumber(json.discountPoints),
        interestRate: toBigNumber(json.interestRate),
        compensation: toBigNumber(json.compensation),
        fundingDate: LocalDate.parse(json.fundingDate),
        upfrontPrePaidItems: toBigNumber(json.upfrontPrePaidItems),
        downpaymentDue: toBigNumber(json.downpaymentDue),
        amountDue: toBigNumber(json.amountDue),
        amounts: {
            base: toBigNumber(json.amounts.base),
            adjusted: toBigNumber(json.amounts.adjusted),
            total: toBigNumber(json.amounts.total),
            final: toBigNumber(json.amounts.final)
        }
    };
}
function prepaidItemFromJson(json) {
    switch(json.type){
        case "FlatFee":
            return {
                ...json,
                amount: toBigNumber(json.amount)
            };
        case "DiscountPoints":
            return {
                ...json,
                amount: toBigNumber(json.amount),
                percentage: {
                    beforeCompensation: toBigNumber(json.percentage.beforeCompensation),
                    afterCompensation: toBigNumber(json.percentage.afterCompensation)
                }
            };
        case "PrepaidInterest":
            return {
                ...json,
                amount: toBigNumber(json.amount),
                period: Period.parse(json.period)
            };
        case "FhaUfmipFee":
        case "SellerCredit":
            return {
                ...json,
                amount: toBigNumber(json.amount)
            };
        case "VaFundingFee":
            return {
                ...json,
                amount: toBigNumber(json.amount)
            };
        case "PropertyTax":
        case "HoaFee":
        case "PropertyInsurance":
        case "MortgageInsurance":
            return {
                ...json,
                period: Period.parse(json.period),
                amount: toBigNumber(json.amount)
            };
        default:
            throw new Error(`Unknown prepaid item: ${JSON.stringify(json)}`);
    }
}
function addWorkDays(date, workDays) {
    const sign = workDays > 0 ? 1 : -1;
    let remaining = Math.abs(workDays);
    let result = date;
    while(remaining > 0){
        result = result.plusDays(sign);
        const dow = result.dayOfWeek();
        if (dow !== DayOfWeek.SATURDAY && dow !== DayOfWeek.SUNDAY) remaining--;
    }
    return result;
}
const endOfMonth = {
    adjustInto (temporal) {
        if (temporal instanceof LocalDate) return temporal.withDayOfMonth(temporal.lengthOfMonth());
        throw new Error("Unsupported temporal type");
    }
};
const firstMonday = {
    adjustInto (temporal) {
        if (temporal instanceof LocalDate) {
            let monday = temporal;
            while(true){
                if (monday.dayOfWeek() === DayOfWeek.MONDAY) return monday;
                monday = monday.plusDays(1);
            }
        }
        throw new Error("Unsupported temporal type");
    }
};
const insuranceTable = [
    [
        14,
        14,
        11,
        10,
        9,
        8,
        7,
        6,
        5,
        4,
        3,
        14
    ],
    [
        14,
        14,
        14,
        11,
        10,
        9,
        8,
        7,
        6,
        5,
        4,
        3
    ],
    [
        3,
        14,
        14,
        14,
        11,
        10,
        9,
        8,
        7,
        6,
        5,
        4
    ],
    [
        4,
        3,
        14,
        14,
        14,
        11,
        10,
        9,
        8,
        7,
        6,
        5
    ],
    [
        5,
        4,
        3,
        14,
        14,
        14,
        11,
        10,
        9,
        8,
        7,
        6
    ],
    [
        6,
        5,
        4,
        3,
        14,
        14,
        14,
        11,
        10,
        9,
        8,
        7
    ],
    [
        7,
        6,
        5,
        4,
        3,
        14,
        14,
        14,
        11,
        10,
        9,
        8
    ],
    [
        8,
        7,
        6,
        5,
        4,
        3,
        14,
        14,
        14,
        11,
        10,
        9
    ],
    [
        9,
        8,
        7,
        6,
        5,
        4,
        3,
        14,
        14,
        14,
        11,
        10
    ],
    [
        10,
        9,
        8,
        7,
        6,
        5,
        4,
        3,
        14,
        14,
        14,
        11
    ],
    [
        11,
        10,
        9,
        8,
        7,
        6,
        5,
        4,
        3,
        14,
        14,
        14
    ],
    [
        14,
        11,
        10,
        9,
        8,
        7,
        6,
        5,
        4,
        3,
        14,
        14
    ]
];
function getPropertyInsuranceDue(fundingDate, renewalDate) {
    const firstPaymentMonth = fundingDate.withDayOfMonth(1).plusMonths(2).monthValue();
    return insuranceTable[firstPaymentMonth - 1][renewalDate.monthValue() - 1];
}
const TaxDeadlines = {
    CO: (year)=>[
            {
                name: "Semi-annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.FEBRUARY, 1).with(endOfMonth)
                    },
                    {
                        dueDate: LocalDate.of(year, Month.JUNE, 15)
                    }
                ]
            },
            {
                name: "Annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.APRIL, 30)
                    }
                ]
            }
        ],
    MN: (year)=>[
            {
                name: "Semi-annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.MAY, 15)
                    },
                    {
                        dueDate: LocalDate.of(year, Month.OCTOBER, 15)
                    }
                ]
            }
        ],
    FL: (year)=>[
            {
                name: "Annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.NOVEMBER, 1)
                    }
                ]
            }
        ],
    TX: (year)=>[
            {
                name: "Annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.JANUARY, 31)
                    }
                ]
            }
        ],
    TN: (year)=>[
            {
                name: "Annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.OCTOBER, 1).with(firstMonday)
                    }
                ]
            }
        ],
    AZ: (year)=>[
            {
                name: "Semi-annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.MARCH, 1)
                    },
                    {
                        dueDate: LocalDate.of(year, Month.OCTOBER, 1)
                    }
                ]
            },
            {
                name: "Annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.DECEMBER, 31)
                    }
                ]
            }
        ],
    WA: (year)=>[
            {
                name: "Semi-annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.APRIL, 30)
                    },
                    {
                        dueDate: LocalDate.of(year, Month.OCTOBER, 31)
                    }
                ]
            },
            {
                name: "Annual",
                payments: [
                    {
                        dueDate: LocalDate.of(year, Month.APRIL, 30)
                    }
                ]
            }
        ]
};
const PropertyTaxPaymentSchema = zod.object({
    dueDate: zod.string().date(),
    isPaid: zod.boolean()
});
function propertyTaxToJson(tax) {
    return {
        ...tax,
        dueDate: tax.dueDate.toString()
    };
}
function propertyTaxFromJson(tax) {
    return {
        ...tax,
        dueDate: LocalDate.parse(tax.dueDate)
    };
}
function getTaxOptions(lastPaymentDate, firstPaymentDate, state) {
    const fn = TaxDeadlines[state];
    if (void 0 === fn) return [];
    const options = [];
    for(let year = lastPaymentDate.year(); year <= firstPaymentDate.year() + 1; year++){
        const yearOptions = fn(year);
        for (const yearOption of yearOptions){
            const total = options.find((x)=>x.name === yearOption.name);
            const payments = yearOption.payments.map((x)=>({
                    ...x,
                    isPaid: x.dueDate.compareTo(lastPaymentDate) < 0
                }));
            if (total) total.payments.push(...payments);
            else options.push({
                name: yearOption.name,
                payments
            });
        }
    }
    return options;
}
function getPrepaidTaxMonthsDue(lastPaymentDate, firstPaymentDate, state, tax) {
    if (!tax) return null;
    if ("override" === tax.type) return tax.value;
    let payments = [];
    if ("custom" === tax.type) payments = tax.value;
    if ("automatic" === tax.type) {
        const options = getTaxOptions(lastPaymentDate, firstPaymentDate, state);
        if (0 === options.length) return null;
        const selected = options.find((x)=>x.name === tax.value);
        if (!selected) return null;
        payments = selected.payments;
    }
    function getPaymentMonths(payment) {
        const paymentsThisYear = payments.filter((x)=>x.dueDate.year() === payment.dueDate.year());
        return Math.floor(12 / paymentsThisYear.length);
    }
    const overduePayments = payments.filter((x)=>!x.isPaid && x.dueDate.isBefore(firstPaymentDate));
    const overdueMonths = overduePayments.reduce((sum, payment)=>sum + getPaymentMonths(payment), 0);
    let monthsInEscrow = 0;
    let prepaidEscrow = 0;
    for(let i = 0; i < 12; i++){
        const yearMonth = YearMonth.from(firstPaymentDate.plusMonths(i));
        monthsInEscrow++;
        const currentMonthPayments = payments.filter((x)=>!x.isPaid && YearMonth.from(x.dueDate).equals(yearMonth));
        for (const payment of currentMonthPayments)monthsInEscrow -= getPaymentMonths(payment);
        if (monthsInEscrow <= 2) {
            const bufferNeeded = 2 - monthsInEscrow;
            if (bufferNeeded > prepaidEscrow) prepaidEscrow = bufferNeeded;
        }
    }
    return prepaidEscrow + overdueMonths;
}
const DEBT_TYPES = [
    "credit_card",
    "second_mortgage",
    "heloc",
    "home_equity_loan",
    "auto_loan",
    "student_loan",
    "personal_loan",
    "medical",
    "other"
];
const DEBT_TYPE_LABELS = {
    credit_card: "Credit Card",
    second_mortgage: "2nd Mortgage",
    heloc: "HELOC",
    home_equity_loan: "Home Equity Loan",
    auto_loan: "Auto Loan",
    student_loan: "Student Loan",
    personal_loan: "Personal Loan",
    medical: "Medical Debt",
    other: "Other"
};
function liabilityFromJSON(json) {
    return {
        balance: toBigNumber(json.balance),
        interestRate: toBigNumber(json.interestRate),
        monthlyPayment: toBigNumber(json.monthlyPayment),
        monthsLeft: json.monthsLeft,
        allPayments: json.allPayments?.map((x)=>({
                balance: toBigNumber(x.balance),
                monthlyPrincipal: toBigNumber(x.monthlyPrincipal),
                monthlyInterest: toBigNumber(x.monthlyInterest)
            })) ?? null
    };
}
function remortgageFromJSON(json) {
    return {
        currentBalance: toBigNumber(json.currentBalance),
        currentMonthlyPayment: toBigNumber(json.currentMonthlyPayment),
        currentPayments: json.currentPayments.map((x)=>({
                balance: toBigNumber(x.balance),
                monthlyPrincipal: toBigNumber(x.monthlyPrincipal),
                monthlyInterest: toBigNumber(x.monthlyInterest)
            })),
        prepays: {
            propertyTax: {
                months: json.prepays.propertyTax.months,
                amount: toBigNumber(json.prepays.propertyTax.amount)
            },
            propertyInsurance: {
                months: json.prepays.propertyInsurance.months,
                amount: toBigNumber(json.prepays.propertyInsurance.amount)
            }
        },
        debts: json.debts.map((debt)=>({
                ...liabilityFromJSON(debt),
                id: debt.id,
                type: debt.type,
                name: debt.name
            })),
        blendedRate: toBigNumber(json.blendedRate)
    };
}
function mortgageFromJSON(value) {
    return Object.freeze({
        ID: value.ID,
        addressID: value.addressID,
        clientID: value.clientID,
        offerIDs: value.offerIDs,
        uploadIDs: value.uploadIDs,
        note: value.note,
        status: value.status,
        updatedAt: Instant.parse(value.updatedAt),
        fees: value.fees,
        data: {
            ...liabilityFromJSON(value.data),
            escrowBalance: toBigNumber(value.data.escrowBalance),
            insuranceEffectiveDate: value.data.insuranceEffectiveDate ? LocalDate.parse(value.data.insuranceEffectiveDate) : null,
            pi: toBigNumber(value.data.pi),
            pimi: toBigNumber(value.data.pimi),
            piti: toBigNumber(value.data.piti),
            tax: mortgageTaxFromJSON(value.data.tax),
            skippedPayments: {
                months: value.data.skippedPayments.months.map((x)=>LocalDate.parse(x)),
                amount: toBigNumber(value.data.skippedPayments.amount)
            },
            escrowRefund: toBigNumber(value.data.escrowRefund),
            payments: {
                principal: toBigNumber(value.data.payments.principal),
                interest: toBigNumber(value.data.payments.interest),
                escrow: toBigNumber(value.data.payments.escrow)
            },
            escrowBreakdown: {
                propertyInsurance: toBigNumber(value.data.escrowBreakdown.propertyInsurance),
                mortgageInsurance: toBigNumber(value.data.escrowBreakdown.mortgageInsurance),
                propertyTax: toBigNumber(value.data.escrowBreakdown.propertyTax)
            },
            payoff: {
                lastPaymentDate: LocalDate.parse(value.data.payoff.lastPaymentDate),
                estClosingDate: LocalDate.parse(value.data.payoff.estClosingDate),
                fundingDate: LocalDate.parse(value.data.payoff.fundingDate),
                fundingDateModified: value.data.payoff.fundingDateModified,
                firstPaymentDate: LocalDate.parse(value.data.payoff.firstPaymentDate),
                interestDays: value.data.payoff.interestDays,
                interestDue: toBigNumber(value.data.payoff.interestDue),
                total: toBigNumber(value.data.payoff.total)
            },
            totalInterestRemaining: toBigNumber(value.data.totalInterestRemaining)
        },
        remortgage: remortgageFromJSON(value.remortgage)
    });
}
const RFQ_STATUSES = [
    "prospects",
    "quoted",
    "closed",
    "archived"
];
const RFQ_STATUS_LABELS = {
    prospects: "Prospects",
    quoted: "Quoted",
    closed: "Closed",
    archived: "Archived"
};
const DebtSchema = z.object({
    type: z["enum"](DEBT_TYPES),
    name: z.string().min(1, "Name is required"),
    balance: BigNumberSchema.min(0, "Please enter at least 0"),
    interest_rate: BigNumberSchema.nonnegative("Please enter a positive number."),
    monthly_payment: BigNumberSchema.min(0, "Please enter at least 0")
});
const MortgagePatchBodySchema = z.object({
    note: z.string().optional(),
    status: z["enum"](RFQ_STATUSES).optional(),
    debts: z.array(DebtSchema).optional(),
    monthlyPropertyInsurance: BigNumberSchema.nonnegative("Monthly property insurance must be a positive number.").optional(),
    monthlyMortgageInsurance: BigNumberSchema.nonnegative("Monthly mortgage insurance must be a positive number.").optional(),
    escrowBalance: BigNumberSchema.optional(),
    insuranceEffectiveDate: z.string().date().optional().nullable(),
    balance: BigNumberSchema.nonnegative("Mortgage balance must be a positive number.").optional(),
    interestRate: BigNumberSchema.nonnegative("Interest rate must be a positive number.").optional(),
    principalPayment: BigNumberSchema.nonnegative("Principal payment must be a positive number.").optional(),
    interestPayment: BigNumberSchema.nonnegative("Interest payment must be a positive number.").optional(),
    escrowPayment: BigNumberSchema.nonnegative("Escrow payment must be a positive number.").optional(),
    tax: z.discriminatedUnion("type", [
        z.object({
            type: z.literal("automatic"),
            value: z.string()
        }),
        z.object({
            type: z.literal("custom"),
            value: PropertyTaxPaymentSchema.array().min(1)
        }),
        z.object({
            type: z.literal("override"),
            value: z.number().nonnegative()
        })
    ]).nullable().optional()
});
function mortgageTaxFromJSON(json) {
    if (json?.type === "custom") return {
        ...json,
        value: json.value.map((x)=>({
                ...x,
                dueDate: toLocalDate(x.dueDate)
            }))
    };
    return json;
}
function offerFromJSON(value) {
    const data = value.data;
    return Object.freeze({
        ID: value.ID,
        mortgageID: value.mortgageID,
        name: value.name,
        investor: value.investor,
        data: {
            name: data.name,
            investor: data.investor,
            prepaidItems: data.prepaidItems.map(prepaidItemFromJson),
            term: data.term,
            discountPoints: toBigNumber(data.discountPoints),
            interestRate: toBigNumber(data.interestRate),
            compensation: toBigNumber(data.compensation),
            fundingDate: LocalDate.parse(data.fundingDate),
            upfrontPrePaidItems: toBigNumber(data.upfrontPrePaidItems),
            amountDue: toBigNumber(data.amountDue),
            amounts: {
                base: toBigNumber(data.amounts.base),
                adjusted: toBigNumber(data.amounts.adjusted),
                total: toBigNumber(data.amounts.total),
                final: toBigNumber(data.amounts.final)
            },
            warnings: data.warnings,
            years: data.years,
            balance: toBigNumber(data.balance),
            bankDiscount: toBigNumber(data.bankDiscount),
            customerDiscountPercentage: toBigNumber(data.customerDiscountPercentage),
            customerDiscount: toBigNumber(data.customerDiscount),
            mortgageInsurance: {
                percentage: toBigNumber(data.mortgageInsurance.percentage),
                annually: toBigNumber(data.mortgageInsurance.annually),
                monthly: toBigNumber(data.mortgageInsurance.monthly)
            },
            prepaidInterest: {
                days: data.prepaidInterest.days,
                due: toBigNumber(data.prepaidInterest.due)
            },
            fees: {
                discountPoints: toBigNumber(data.fees.discountPoints),
                list: data.fees.list.map((fee)=>({
                        name: fee.name,
                        amount: toBigNumber(fee.amount)
                    }))
            },
            closingCosts: toBigNumber(data.closingCosts),
            payoff: toBigNumber(data.payoff),
            checkbookDifference: toBigNumber(data.checkbookDifference),
            interestSavings: toBigNumber(data.interestSavings),
            totalSavings: toBigNumber(data.totalSavings),
            pi: toBigNumber(data.pi),
            pimi: toBigNumber(data.pimi),
            piti: toBigNumber(data.piti),
            allPayments: data.allPayments?.map((x)=>({
                    balance: toBigNumber(x.balance),
                    monthlyPrincipal: toBigNumber(x.monthlyPrincipal),
                    monthlyInterest: toBigNumber(x.monthlyInterest)
                })) ?? null,
            totalInterest: toBigNumber(data.totalInterest),
            prepaysTotal: toBigNumber(data.prepaysTotal),
            monthlySavings: {
                amount: toBigNumber(data.monthlySavings.amount),
                breakEvenMonths: toBigNumber(data.monthlySavings.breakEvenMonths),
                breakEvenDate: toLocalDate(data.monthlySavings.breakEvenDate)
            },
            firstYearInterestSavings: {
                amount: toBigNumber(data.firstYearInterestSavings.amount),
                breakEvenMonths: toBigNumber(data.firstYearInterestSavings.breakEvenMonths),
                breakEvenDate: toLocalDate(data.firstYearInterestSavings.breakEvenDate)
            }
        }
    });
}
function profileFromJSON(json) {
    return {
        ...json,
        subscription: json.subscription ? {
            startAt: Instant.parse(json.subscription.startAt),
            endAt: Instant.parse(json.subscription.endAt),
            cancelAt: json.subscription.cancelAt ? Instant.parse(json.subscription.cancelAt) : void 0
        } : void 0
    };
}
const localDateRefine = (value)=>{
    try {
        LocalDate.parse(value);
        return true;
    } catch  {
        return false;
    }
};
const SessionRequestBodySchema = z.object({
    email: z.string({
        required_error: "Email address is required."
    }).email({
        message: "Please enter a valid email address."
    }).transform((x)=>x.toLowerCase())
});
const SessionConsumeBodySchema = z.object({
    id: z.number({
        required_error: "ID is required."
    }).positive("ID must be positive"),
    secret: z.string({
        required_error: "Secret is required"
    })
});
const RegistrationBodySchema = z.object({
    companyName: z.string().min(1, "Company name is required"),
    companyNmls: z.string().min(1, "Company NMLS number is required"),
    companyAddress: z.string().min(1, "Company address is required"),
    mpName: z.string().min(1, "Name is required"),
    mpEmail: z.string().email("Please enter a valid email address").transform((x)=>x.toLowerCase()),
    mpPhone: z.string().min(1, "Phone number is required").refine((x)=>{
        try {
            const parsed = parsePhoneNumberWithError(x, {
                defaultCountry: "US"
            });
            return parsed.isValid();
        } catch  {
            return false;
        }
    }, "Enter a valid phone number").transform((x)=>{
        const parsed = parsePhoneNumberWithError(x, {
            defaultCountry: "US"
        });
        return parsed.formatInternational();
    }),
    mpNmls: z.string().min(1, "NMLS number is required"),
    mpLicenseNumber: z.string().min(1, "License number is required")
});
const BorrowerNewBodySchema = z.object({
    firstName: z.string({
        required_error: "Please enter first name."
    }).trim().min(2, "First name must be at least 2 characters long."),
    lastName: z.string({
        required_error: "Please enter last name."
    }).trim().min(2, "Last name must be at least 2 characters long."),
    email: z.string({
        required_error: "Please enter email address."
    }).trim().email({
        message: "Please enter a valid email address."
    }).transform((value)=>value.toLowerCase()),
    creditScore: z.preprocess((v)=>{
        if (null == v) return null;
        if ("string" == typeof v && "" === v.trim()) return null;
        return v;
    }, z.number().min(1, "Credit score can not be lower than 1").max(999, "Credit score can not be greater than 999").nullable())
});
const DateBodySchema = z.object({
    date: z.string({
        required_error: "Please enter date."
    }).date("Please enter a valid date.")
});
const AddressNewBodySchema = z.object({
    line1: z.string({
        required_error: "Please enter address line 1."
    }).trim().nonempty("Address line 1 cannot be empty."),
    line2: z.string({
        required_error: "Please enter address line 2."
    }).trim().transform((x)=>{
        if ("" === x) return;
        return x;
    }).optional(),
    city: z.string({
        required_error: "Please enter city."
    }).trim().nonempty("City cannot be empty."),
    state: StateSchema,
    zipCode: z.string({
        required_error: "Please enter zip code."
    }).trim().nonempty("Zip code cannot be empty.").regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code. Use 5 digits (e.g. “12345”) or ZIP+4 (e.g. “12345-6789”).")
});
const AddressUpdateValueBodySchema = z.object({
    amount: BigNumberSchema.nonnegative("Value must be a positive number.")
});
const AddressUpdateTaxBodySchema = z.object({
    amount: z.number({
        required_error: "Please enter amount."
    }).nonnegative("Amount must be a positive number.").lt(1000000, "Amount can not be more than 1 million."),
    year: z.coerce.number({
        required_error: "Please enter address line 2."
    }).min(2000, "Year must be at least 2000.").max(2050, "Year can not be more than 2050.")
});
const AddressPlacesSearchBodySchema = z.object({
    query: z.string().trim().min(1, "Search query is required."),
    sessionToken: z.string().min(1, "Session token is required.")
});
const AddressPlacesSelectBodySchema = z.object({
    placeId: z.string().min(1, "Place ID is required."),
    sessionToken: z.string().min(1, "Session token is required.")
});
const NewRFQBodySchema = z.object({
    borrowerID: z.number({
        required_error: "Borrower is required."
    }),
    addressID: z.number({
        required_error: "Address is required."
    }),
    balance: BigNumberSchema.nonnegative("Balance must be a positive number.").max(10000000000000, "Balance can not be more than 10 quadrillion dollars."),
    escrowBalance: BigNumberSchema.max(10000000000000, "Balance can not be more than 10 quadrillion dollars."),
    interestRate: BigNumberSchema.nonnegative("Interest rate must be a positive number.").max(100, "Interest rate can not be 100% or more."),
    principal: BigNumberSchema.nonnegative("Principal payment must be a positive number."),
    interest: BigNumberSchema.nonnegative("Interest payment must be a positive number."),
    escrow: BigNumberSchema.nonnegative("Escrow payment must be a positive number."),
    insuranceEffectiveDate: z.string().date("Please enter a valid date.").optional()
});
const MORTGAGE_TYPES = [
    "CONVENTIONAL",
    "FHA",
    "VA"
];
const PurchaseFieldsSchema = z.object({
    mortgageType: z["enum"](MORTGAGE_TYPES, {
        required_error: "Mortgage type is required."
    }),
    price: BigNumberSchema.nonnegative("Price must be a positive number.").max(10000000000000, "Price can not be more than 10 quadrillion dollars."),
    downpayment: BigNumberSchema.nonnegative("Downpayment must be a positive number."),
    earnest: BigNumberSchema.nonnegative("Earnest must be a positive number."),
    sellerCredit: BigNumberSchema.nonnegative("Seller credit must be a positive number."),
    fundingDate: z.string().nonempty("Funding date is required.").refine(localDateRefine, {
        message: "Funding date is invalid."
    }),
    annualInsurance: BigNumberSchema.nonnegative("Annual insurance must be a positive number."),
    annualHOA: BigNumberSchema.nonnegative("Annual HOA must be a positive number."),
    annualTax: BigNumberSchema.nonnegative("Annual tax must be a positive number.").nullable(),
    vaFirstTime: z.boolean().nullable().default(null),
    vaExempt: z.boolean().nullable().default(null)
});
const purchaseSuperRefine = (data, ctx)=>{
    if ("VA" === data.mortgageType && null === data.vaFirstTime) ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "VA first time use is required for VA loans.",
        path: [
            "vaFirstTime"
        ]
    });
    if ("VA" === data.mortgageType && null === data.vaExempt) ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "VA exempt status is required for VA loans.",
        path: [
            "vaExempt"
        ]
    });
    const hasAddress = "addressID" in data ? null != data.addressID : true;
    if (!hasAddress && (!data.annualTax || data.annualTax.lte(0))) ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Annual tax is required for TBD addresses.",
        path: [
            "annualTax"
        ]
    });
};
const NewPurchaseBodySchema = PurchaseFieldsSchema.extend({
    borrowerID: z.number({
        required_error: "Borrower is required."
    }),
    addressID: z.number().nullable()
}).superRefine(purchaseSuperRefine);
const EditPurchaseBodySchema = PurchaseFieldsSchema.extend({
    addressID: z.number().nullable(),
    note: z.string().default("")
}).superRefine(purchaseSuperRefine);
const PurchasePatchBodySchema = z.object({
    status: z["enum"](RFQ_STATUSES).optional()
});
const FeeSchema = z.object({
    name: z.string().min(1, "Fee name is required"),
    amount: BigNumberSchema.min(0, "Please enter at least 0")
});
const OfferItemSchema = z.object({
    ID: z.number().nullable(),
    name: z.string().min(2, "Please enter at least 2 characters."),
    investor: z.string(),
    interest_rate: BigNumberSchema.nonnegative("Please enter a positive number."),
    discount_points: BigNumberSchema
});
const ManageOfferBodySchema = z.object({
    offers: z.array(OfferItemSchema).min(1, "Add at least one offer"),
    mortgage_insurance: BigNumberSchema.nonnegative("Please enter a positive number."),
    compensation: BigNumberSchema.nonnegative("Compensation can not be negative."),
    amount_due: BigNumberSchema,
    years: z.coerce.number().min(5).max(50),
    fees: z.array(FeeSchema)
});
const EditMortgageTimelineBodySchema = z.object({
    lastPaymentDate: z.string().nonempty("Last payment date is required.").refine(localDateRefine, {
        message: "Last payment date is invalid."
    }),
    estClosingDate: z.string().nonempty("Est. closing date is required.").refine(localDateRefine, {
        message: "Est. closing date is invalid."
    }),
    fundingDate: z.string().nonempty("Funding date is required.").refine(localDateRefine, {
        message: "Funding date is invalid."
    })
}).refine((data)=>data.estClosingDate >= data.lastPaymentDate, {
    message: "Est. closing date must be after last payment date.",
    path: [
        "estClosingDate"
    ]
}).refine((data)=>data.fundingDate >= data.estClosingDate, {
    message: "Funding date must be after est. closing date.",
    path: [
        "fundingDate"
    ]
});
const ProfilePatchBodySchema = z.object({
    firstName: z.string().min(1, "First name is required").optional(),
    lastName: z.string().min(1, "Last name is required").optional(),
    phone: z.string().min(1, "Phone number is required").refine((x)=>{
        try {
            const parsed = parsePhoneNumberWithError(x, {
                defaultCountry: "US"
            });
            return parsed.isValid();
        } catch  {
            return false;
        }
    }, "Enter a valid phone number").transform((x)=>{
        const parsed = parsePhoneNumberWithError(x, {
            defaultCountry: "US"
        });
        return parsed.formatInternational();
    }).optional(),
    nmls: z.string().min(1, "NMLS number is required").optional(),
    licenseNumber: z.string().min(1, "License number is required").optional()
});
const CompanyPatchBodySchema = z.object({
    name: z.string().min(1, "Company name is required").optional(),
    nmls: z.string().min(1, "Company NMLS is required").optional(),
    address: z.string().min(1, "Company address is required").optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional()
});
const CompanyFeesPutBodySchema = z.object({
    fees: z.array(FeeSchema).min(1, "Enter at least one fee")
});
class HttpInterface {
    http;
    constructor(config){
        this.http = axios.create(config);
    }
    RequestSession(req) {
        return this.http.post("/api/auth/request", req);
    }
    ConsumeSessionRequest(req) {
        return this.http.post("/api/auth/consume", req);
    }
    Profile() {
        return this.http.get("/api/auth/profile").then((x)=>profileFromJSON(x.data));
    }
    ProfilePatch(body) {
        return this.http.patch("/api/auth/profile", body);
    }
    CompanyPatch(body, logo) {
        const formData = new FormData();
        const keys = Object.keys(body);
        for (const key of keys){
            const value = body[key];
            if (void 0 !== value) formData.append(key, value);
        }
        if (logo) formData.append("logo", logo);
        return this.http.patch("/api/auth/company", formData);
    }
    CompanyFees() {
        return this.http.get("/api/auth/company/fees").then((x)=>x.data);
    }
    CompanyFeesPut(body) {
        return this.http.put("/api/auth/company/fees", body);
    }
    Logout() {
        return this.http.post("/api/auth/logout");
    }
    Register(body) {
        return this.http.post("/api/auth/register", body);
    }
    MortgageList() {
        return this.http.get("/api/mortgage");
    }
    MortgageNew(body) {
        return this.http.post("/api/mortgage", body);
    }
    Mortgage(IDs) {
        return this.http.get(`/api/mortgage/${IDs.join()}`).then(({ data })=>data.map((x)=>mortgageFromJSON(x)));
    }
    MortgageDelete(mortgageID) {
        return this.http.delete(`/api/mortgage/${mortgageID}`);
    }
    MortgageUpdateTimeline(mortgageID, body) {
        return this.http.put(`/api/mortgage/${mortgageID}/timeline`, body);
    }
    MortgageOffers(IDs) {
        return this.http.get(`/api/mortgage/offer/${IDs.join()}`).then(({ data })=>data.map((x)=>offerFromJSON(x)));
    }
    async MortgageOffersManage(mortgageID, body) {
        await this.http.post(`/api/mortgage/offer/${mortgageID}`, body);
    }
    MortgageEditPropertyInsuranceDate(mortgageID, body) {
        return this.http.post(`/api/mortgage/${mortgageID}/insurance-date`, body);
    }
    MortgagePatch(mortgageID, body) {
        return this.http.patch(`/api/mortgage/${mortgageID}`, body);
    }
    PurchaseList() {
        return this.http.get("/api/purchase");
    }
    PurchaseNew(body) {
        return this.http.post("/api/purchase", body);
    }
    Purchase(IDs) {
        return this.http.get(`/api/purchase/${IDs.join()}`).then(({ data })=>data);
    }
    PurchaseDelete(purchaseID) {
        return this.http.delete(`/api/purchase/${purchaseID}`);
    }
    PurchaseEdit(purchaseID, body) {
        return this.http.put(`/api/purchase/${purchaseID}`, body);
    }
    PurchasePatch(purchaseID, body) {
        return this.http.patch(`/api/purchase/${purchaseID}`, body);
    }
    PurchaseOffers(IDs) {
        return this.http.get(`/api/purchase/offer/${IDs.join()}`).then(({ data })=>data.map((x)=>({
                    ...x,
                    offer: purchaseOfferFromJSON(x.offer)
                })));
    }
    PurchaseOffersManage(purchaseID, body) {
        return this.http.post(`/api/purchase/offer/${purchaseID}`, body);
    }
    BorrowerList() {
        return this.http.get("/api/borrower");
    }
    BorrowerSearch(query) {
        return this.http.get(`/api/borrower/search/${encodeURIComponent(query)}`);
    }
    Borrower(IDs) {
        return this.http.get(`/api/borrower/${IDs.join()}`);
    }
    BorrowerNew(body) {
        return this.http.post("/api/borrower", body);
    }
    BorrowerEdit(borrowerID, body) {
        return this.http.put(`/api/borrower/${borrowerID}`, body);
    }
    Address(IDs) {
        return this.http.get(`/api/address/${IDs.join()}`).then((res)=>res.data.map((x)=>addressFromJSON(x)));
    }
    AddressUpdateValue(addressID, body) {
        return this.http.patch(`/api/address/${addressID}/value`, body);
    }
    AddressSearch(query) {
        return this.http.get(`/api/address/search/${query}`);
    }
    AddressPlacesSearch(body) {
        return this.http.post("/api/address/places/search", body);
    }
    AddressPlacesSelect(body) {
        return this.http.post("/api/address/places/select", body).then((res)=>addressFromJSON(res.data));
    }
    AddressNew(body) {
        return this.http.post("/api/address", body);
    }
    AddressUpdateTax(addressID, body) {
        return this.http.patch(`/api/address/${addressID}/tax`, body);
    }
    Submit(files) {
        const formData = new FormData();
        for (const file of files)formData.append("files", file);
        return this.http.post("/api/mortgage/submit", formData);
    }
    async SubmitComplete(token, body) {
        const x = await this.http.post(`/api/mortgage/submit/${token}/complete`, body);
        return x.data;
    }
    Upload(IDs) {
        return this.http.get(`/api/upload/${IDs.join()}`);
    }
    BugReport(body) {
        return this.http.post("/api/help/bug-report", body);
    }
    StripeCheckout() {
        return this.http.post("/api/subscription/checkout");
    }
    StripePricing() {
        return this.http.get("/api/subscription/pricing");
    }
    StripePortal() {
        return this.http.post("/api/subscription/portal");
    }
}
function pmt_pmt(rate, numberOfPayments, presentValue, futureValue = bignumber(0), type = "end") {
    const T = new bignumber("end" === type ? 0 : 1);
    const _numberOfPayments = new bignumber(numberOfPayments);
    if (rate.isZero()) return presentValue.plus(futureValue).dividedBy(_numberOfPayments).negated();
    const onePlusRpowN = rate.plus(1).pow(_numberOfPayments);
    const num = rate.times(presentValue.times(onePlusRpowN).plus(futureValue));
    const den = rate.times(T).plus(1).times(onePlusRpowN.minus(1));
    return num.dividedBy(den).negated().decimalPlaces(2);
}
function calculatePayoffInterest(balance, interestRate, lastPaymentDate, fundingDate) {
    const days = lastPaymentDate.until(fundingDate, ChronoUnit.DAYS).valueOf() + 6;
    const dailyInterestDate = new bignumber(interestRate).dividedBy(100).div(365);
    const due = new bignumber(balance).multipliedBy(dailyInterestDate).multipliedBy(days).dp(2);
    return {
        days,
        due
    };
}
function nper(rate, pmt, pv) {
    const R = new bignumber(rate);
    const PMT = new bignumber(pmt);
    const PV = new bignumber(pv);
    if (!R.isFinite() || !PMT.isFinite() || !PV.isFinite()) return null;
    if (PMT.isZero()) return null;
    const ZERO_RATE_EPS = 1e-12;
    if (R.abs().lte(ZERO_RATE_EPS)) return PV.negated().div(PMT);
    const onePlusR = R.plus(1);
    if (onePlusR.lte(0)) return null;
    if (R.gt(0) && PV.gt(0) && PMT.gte(PV.times(R).negated())) return null;
    const denom = PMT.plus(R.times(PV));
    if (denom.isZero()) return null;
    const ratio = PMT.div(denom);
    const ratioNum = ratio.toNumber();
    const baseNum = onePlusR.toNumber();
    if (!(ratioNum > 0) || !(baseNum > 0)) return null;
    const n = Math.log(ratioNum) / Math.log(baseNum);
    return new bignumber(n);
}
function getFirstPaymentDate(closingDate) {
    return closingDate.withDayOfMonth(1).plusMonths(2);
}
function getSkippedPayments(lastPaymentDate, closingDate) {
    const firstPaymentDate = getFirstPaymentDate(closingDate);
    const skippedMonths = [];
    let current = lastPaymentDate.plusDays(1);
    while(!current.equals(firstPaymentDate)){
        if (1 === current.dayOfMonth()) skippedMonths.push(current);
        current = current.plusDays(1);
    }
    return skippedMonths;
}
function estimateOfferBalance(values) {
    const interestRateFloat = values.interestRate.dividedBy(100);
    const prepaidInterestDays = getPrepaidInterestDays(values.fundingDate);
    const ratio = new bignumber(1).minus(interestRateFloat.multipliedBy(prepaidInterestDays.days()).dividedBy(365).plus(values.discountPointsPercentage.dividedBy(100)));
    const fixedCosts = values.currentMortgageBalance.plus(values.closingCost).plus(values.insurance).plus(values.taxes).plus(values.compensation).plus(values.otherDebtsTotal).minus(values.amountDue);
    let estimate = fixedCosts.dividedBy(ratio).dp(2);
    let lastDiff;
    const step = new bignumber(0.01);
    let i = 0;
    for(;;){
        if (i >= 10000) throw new Error("Too many iterations");
        i++;
        const prepaidInterest = estimate.multipliedBy(interestRateFloat).div(365).multipliedBy(prepaidInterestDays.days()).dp(2);
        const customerDiscountPercentage = values.discountPointsPercentage.div(100).plus(values.compensation.dividedBy(estimate).dp(6));
        const customerDiscount = estimate.multipliedBy(customerDiscountPercentage).dp(2);
        const total = values.currentMortgageBalance.plus(values.closingCost).plus(customerDiscount).plus(prepaidInterest).plus(values.taxes).plus(values.insurance).plus(values.otherDebtsTotal).minus(values.amountDue).dp(2);
        const diff = total.comparedTo(estimate);
        if (null === diff) throw new Error("Invalid comparison");
        if (0 === diff) break;
        if (void 0 === lastDiff) lastDiff = diff;
        estimate = estimate.plus(step.multipliedBy(diff));
        if (lastDiff !== diff) {
            if (-1 === lastDiff) estimate = estimate.plus(step);
            break;
        }
    }
    return estimate;
}
function getPrepaidInterestDays(fundingDate) {
    return Period.ofDays(fundingDate.lengthOfMonth() - fundingDate.dayOfMonth() + 1);
}
const ExtractedStatementSchema = z.object({
    borrowers: z.array(z.object({
        firstName: z.string().describe("First name"),
        lastName: z.string().describe("Last name")
    })).describe("The borrower names"),
    propertyAddress: z.object({
        line1: z.string().describe("The property address line 1"),
        line2: z.string().describe("The property address line 2"),
        city: z.string().describe("The property city"),
        state: StateSchema.describe("The property state"),
        zip_code: z.string().describe("The property ZIP code")
    }),
    balance: z.number().describe("The outstanding principal"),
    interestRate: z.number().describe("The interest rate"),
    escrowBalance: z.number().describe("The escrow balance"),
    payment: z.object({
        principal: z.number().describe("The principal due"),
        interest: z.number().describe("The interest due"),
        escrow: z.number().nullable().describe("The escrow due")
    })
});
const SubmitCompleteRequestBodySchema = z.object({
    balance: BigNumberSchema.nonnegative("Balance must be a positive number.").max(10000000000000, "Balance can not be more than 10 quadrillion dollars."),
    escrowBalance: BigNumberSchema.max(10000000000000, "Balance can not be more than 10 quadrillion dollars."),
    interestRate: BigNumberSchema.nonnegative("Interest rate must be a positive number.").max(100, "Interest rate can not be 100% or more."),
    principal: BigNumberSchema.nonnegative("Principal payment must be a positive number."),
    interest: BigNumberSchema.nonnegative("Interest payment must be a positive number."),
    escrow: BigNumberSchema.nonnegative("Escrow payment must be a positive number."),
    insuranceEffectiveDate: z.string().date("Please enter a valid date.").optional(),
    address: AddressNewBodySchema,
    borrower: BorrowerNewBodySchema,
    uploads: z.array(z.number()).optional().default([])
});
const AcceptedFiles = [
    "image/png",
    "image/webp",
    "image/jpg",
    "image/jpeg",
    "application/pdf"
];
function formatMonth(date) {
    if (!date) return "-";
    return date.format(MonthFormat);
}
function formatDate(date) {
    if (!date) return "-";
    return date.format(DateFormat);
}
function formatInstantDate(instant) {
    if (!instant) return "-";
    return instant.atZone(ZoneId.systemDefault()).toLocalDate().format(DateFormat);
}
function formatAmount(amount, dp = 2) {
    if (null == amount) return "-";
    const _amount = new bignumber(amount);
    return _amount.toFormat(dp, {
        decimalSeparator: ".",
        groupSeparator: ",",
        groupSize: 3
    });
}
function formatMoney(amount, dp = 2) {
    if (null == amount) return "-";
    return `$${formatAmount(amount, dp)}`;
}
function formatPercent(amount, dp = 3) {
    if (null == amount) return "-";
    return `${formatAmount(amount, dp)}%`;
}
function formatAddress(parts) {
    return parts.filter((x)=>!!x).map((x)=>x?.trim()).join(", ");
}
function getAddressLabel(item) {
    return formatAddress([
        item.line1,
        item.line2,
        item.city,
        item.state,
        item.zip_code
    ]);
}
function src_sum(amounts) {
    if (amounts.includes(void 0) || amounts.includes(null)) return;
    return amounts.map((x)=>new bignumber(x)).reduce((acc, x)=>acc.plus(x), new bignumber(0));
}
function formatBytes(bytes, decimals = 2) {
    if (!+bytes) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = [
        "Bytes",
        "KiB",
        "MiB",
        "GiB",
        "TiB",
        "PiB",
        "EiB",
        "ZiB",
        "YiB"
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}
function pick(value, options) {
    if (null === value) return options[1];
    const dir = value.comparedTo(0);
    if (null === dir) return options[1];
    return options[dir + 1];
}
function formatAmountDueText(due) {
    return pick(due, [
        "Amount received at closing",
        "Amount due at closing",
        "Amount due at closing"
    ]);
}
function formatFundFromToAtClosing(due) {
    return pick(due, [
        "Funds to at closing",
        "Funds from at closing",
        "Funds from at closing"
    ]);
}
const Byte = 1;
const KB = 1024;
const MB = 1048576;
const GB = 1073741824;
export { AcceptedFiles, AddressNewBodySchema, AddressPlacesSearchBodySchema, AddressPlacesSelectBodySchema, AddressUpdateTaxBodySchema, AddressUpdateValueBodySchema, BigNumberSchema, BorrowerNewBodySchema, Byte, CompanyFeesPutBodySchema, CompanyPatchBodySchema, Config, DEBT_TYPES, DEBT_TYPE_LABELS, DateBodySchema, DateFormat, EditMortgageTimelineBodySchema, EditPurchaseBodySchema, ExtractedStatementSchema, FeeSchema, GB, HttpInterface, KB, MB, MORTGAGE_TYPES, ManageOfferBodySchema, MonthFormat, MortgagePatchBodySchema, NewPurchaseBodySchema, NewRFQBodySchema, ProfilePatchBodySchema, PropertyTaxPaymentSchema, PurchasePatchBodySchema, RFQ_STATUSES, RFQ_STATUS_LABELS, RegistrationBodySchema, SessionConsumeBodySchema, SessionRequestBodySchema, StateNames, StateSchema, SubmitCompleteRequestBodySchema, TaxDeadlines, ZERO, addWorkDays, addressFromJSON, calculatePayoffInterest, clamp, endOfMonth, estimateOfferBalance, firstMonday, formatAddress, formatAmount, formatAmountDueText, formatBytes, formatDate, formatFundFromToAtClosing, formatInstantDate, formatMoney, formatMonth, formatPercent, getAddressLabel, getFirstPaymentDate, getPrepaidInterestDays, getPrepaidTaxMonthsDue, getPropertyInsuranceDue, getSkippedPayments, getTaxOptions, liabilityFromJSON, monthsToYears, nper, pick, pmt_pmt as pmt, prepaidItemFromJson, profileFromJSON, propertyTaxFromJson, propertyTaxToJson, purchaseOfferFromJSON, remortgageFromJSON, src_sum as sum, toBigNumber, toLocalDate };
